import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import ReactQuill from 'react-quill';
import { toast } from 'react-toastify';
import 'react-quill/dist/quill.snow.css';
import { uploadDocumentationAttachment, getDocumentationAttachmentDownloadUrl } from '../../services/documentationApi';
import {
  buildDocsAttachmentSrc,
  parseDocsAttachmentId,
} from '../../utils/documentationInlineAttachments';
import { DocumentationEditorToolbar } from './DocumentationEditorToolbar';
import { EmojiPicker } from './EmojiPicker';
import type { CalloutVariant } from './quill/CalloutBlot';
import { createEmptyTableValue } from './quill/TableBlot';
import { registerQuillDocFormats } from './quill/registerQuillDocFormats';
import type { RichTextEditorHandle } from './RichTextEditorHandle';
import { TableInsertModal } from './TableInsertModal';
import { VideoEmbedModal } from './VideoEmbedModal';
import './richTextEditor.css';

registerQuillDocFormats();

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: string;
  allowLocalImages?: boolean;
  maxLocalImageBytes?: number;
  /** Quando informado, imagens vão ao storage (docs-attachment) em vez de data URL. */
  pageId?: string;
  variant?: 'default' | 'documentation';
  /** Título editável (documentação) — fica no bloco fixável com a toolbar. */
  title?: string;
  onTitleChange?: (title: string) => void;
  titlePlaceholder?: string;
};

function normalizeQuillHtml(html: string): string {
  if (!html || html === '<p><br></p>' || html === '<p></p>') {
    return '';
  }
  return html;
}

const DEFAULT_MAX_LOCAL_IMAGE_BYTES = 5 * 1024 * 1024;

const ACCEPTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

function isAcceptedImage(file: File): boolean {
  return ACCEPTED_IMAGE_TYPES.has(file.type) || /\.(png|jpe?g|gif|webp)$/i.test(file.name);
}

function extensionForMime(mime: string): string {
  switch (mime) {
    case 'image/jpeg':
      return '.jpg';
    case 'image/gif':
      return '.gif';
    case 'image/webp':
      return '.webp';
    default:
      return '.png';
  }
}

function resolveImageFileName(file: File): string {
  const raw = file.name?.trim();
  if (raw && /\.[^.]+$/.test(raw)) {
    return raw;
  }
  return `imagem${extensionForMime(file.type || 'image/png')}`;
}

const DOCS_ATTACHMENT_ATTR = 'data-docs-attachment';

/** Converte src resolvido de volta para docs-attachment:{id} antes de persistir. */
function toPersistHtml(html: string): string {
  if (!html || typeof document === 'undefined') return html;
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  wrap.querySelectorAll('img').forEach((img) => {
    const fromAttr = img.getAttribute(DOCS_ATTACHMENT_ATTR);
    const fromSrc = parseDocsAttachmentId(img.getAttribute('src'));
    const id = fromAttr || fromSrc;
    if (!id) return;
    img.setAttribute('src', buildDocsAttachmentSrc(id));
    img.removeAttribute(DOCS_ATTACHMENT_ATTR);
  });
  return wrap.innerHTML;
}

const DEFAULT_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'link',
  'image',
];

const DOCUMENTATION_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'indent',
  'align',
  'blockquote',
  'code-block',
  'link',
  'image',
  'video',
  'divider',
  'callout',
  'collapse',
  'doctable',
];

const CALLOUT_PRESETS: { variant: CalloutVariant; label: string; emoji: string }[] = [
  { variant: 'info', label: 'Informação', emoji: '💡' },
  { variant: 'tip', label: 'Dica', emoji: '✨' },
  { variant: 'warning', label: 'Atenção', emoji: '⚠️' },
  { variant: 'success', label: 'Sucesso', emoji: '✅' },
];

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    {
      value,
      onChange,
      placeholder,
      readOnly = false,
      minHeight = '160px',
      allowLocalImages = false,
      maxLocalImageBytes = DEFAULT_MAX_LOCAL_IMAGE_BYTES,
      pageId,
      variant = 'default',
      title,
      onTitleChange,
      titlePlaceholder = 'Título da página',
    },
    ref,
  ) {
    const quillRef = useRef<ReactQuill>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    const insertImageSrc = useCallback(
      (src: string, attachmentId?: string) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const range = editor.getSelection(true);
        const index = range?.index ?? Math.max(editor.getLength() - 1, 0);
        editor.insertEmbed(index, 'image', src, 'user');
        if (attachmentId) {
          const imgs = editor.root.querySelectorAll('img');
          const last = imgs[imgs.length - 1];
          if (last) {
            last.setAttribute(DOCS_ATTACHMENT_ATTR, attachmentId);
          }
        }
        editor.setSelection(index + 1, 0);
      },
      [],
    );

    const uploadOrEmbedImage = useCallback(
      async (file: File) => {
        if (!isAcceptedImage(file)) {
          toast.error('Use uma imagem PNG, JPEG, GIF ou WebP.');
          return;
        }
        if (file.size > maxLocalImageBytes) {
          toast.error(
            `A imagem deve ter no máximo ${Math.round(maxLocalImageBytes / (1024 * 1024))} MB.`,
          );
          return;
        }

        if (pageId) {
          setUploadingImage(true);
          try {
            const named =
              file.name && /\.[^.]+$/.test(file.name)
                ? file
                : new File([file], resolveImageFileName(file), { type: file.type || 'image/png' });
            const uploaded = await uploadDocumentationAttachment(pageId, named);
            const download = await getDocumentationAttachmentDownloadUrl(pageId, uploaded.id);
            insertImageSrc(download.url, uploaded.id);
          } catch {
            toast.error('Não foi possível enviar a imagem ao storage.');
          } finally {
            setUploadingImage(false);
          }
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = typeof reader.result === 'string' ? reader.result : null;
          if (dataUrl) insertImageSrc(dataUrl);
        };
        reader.readAsDataURL(file);
      },
      [insertImageSrc, maxLocalImageBytes, pageId],
    );
    const [toolbarEl, setToolbarEl] = useState<HTMLDivElement | null>(null);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [calloutOpen, setCalloutOpen] = useState(false);
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [tableModalOpen, setTableModalOpen] = useState(false);
    const [toolbarPinned, setToolbarPinned] = useState(() => {
      try {
        return window.localStorage.getItem('technology.documentation.editor-toolbar-pinned') === '1';
      } catch {
        return false;
      }
    });

    const isDocs = variant === 'documentation';

    function toggleToolbarPin(): void {
      setToolbarPinned((prev) => {
        const next = !prev;
        try {
          window.localStorage.setItem(
            'technology.documentation.editor-toolbar-pinned',
            next ? '1' : '0',
          );
        } catch {
          // ignore
        }
        return next;
      });
    }
    const insertAtCursor = useCallback(
      (action: (editor: ReturnType<ReactQuill['getEditor']>, index: number) => void) => {
        const editor = quillRef.current?.getEditor();
        if (!editor || readOnly) return;
        const range = editor.getSelection(true);
        const index = range?.index ?? Math.max(editor.getLength() - 1, 0);
        action(editor, index);
      },
      [readOnly],
    );

    const docsHandlers = useMemo(() => {
      const handlers: Record<string, () => void> = {};

      if (allowLocalImages) {
        handlers.image = () => {
          if (uploadingImage) return;
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/png,image/jpeg,image/gif,image/webp');
          input.click();
          input.onchange = () => {
            const file = input.files?.[0];
            if (!file) return;
            void uploadOrEmbedImage(file);
          };
        };
      }

      handlers.divider = () => {
        insertAtCursor((editor, index) => {
          editor.insertText(index, '\n', 'user');
          editor.insertEmbed(index + 1, 'divider', true, 'user');
          editor.insertText(index + 2, '\n', 'user');
          editor.setSelection(index + 3, 0);
        });
      };

      handlers.video = () => {
        setEmojiOpen(false);
        setCalloutOpen(false);
        setTableModalOpen(false);
        setVideoModalOpen(true);
      };

      handlers.collapse = () => {
        insertAtCursor((editor, index) => {
          editor.insertText(index, '\n', 'user');
          editor.insertEmbed(
            index + 1,
            'collapse',
            {
              title: 'Seção expansível',
              bodyHtml: '<p>Escreva o conteúdo aqui…</p>',
              open: true,
            },
            'user',
          );
          editor.insertText(index + 2, '\n', 'user');
          editor.setSelection(index + 3, 0);
        });
      };

      handlers.table = () => {
        setEmojiOpen(false);
        setCalloutOpen(false);
        setVideoModalOpen(false);
        setTableModalOpen(true);
      };

      handlers.callout = () => {
        setEmojiOpen(false);
        setVideoModalOpen(false);
        setTableModalOpen(false);
        setCalloutOpen((open) => !open);
      };

      handlers.emoji = () => {
        setCalloutOpen(false);
        setVideoModalOpen(false);
        setTableModalOpen(false);
        setEmojiOpen((open) => !open);
      };

      return handlers;
    }, [allowLocalImages, insertAtCursor, uploadOrEmbedImage, uploadingImage]);

    const modules = useMemo(() => {
      if (readOnly) {
        return { toolbar: false };
      }

      if (isDocs) {
        if (!toolbarEl) {
          return { toolbar: false };
        }
        return {
          toolbar: {
            container: toolbarEl,
            handlers: docsHandlers,
          },
        };
      }

      const handlers: Record<string, () => void> = {};
      if (allowLocalImages) {
        handlers.image = docsHandlers.image;
      }

      return {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            ['clean'],
          ],
          handlers,
        },
      };
    }, [allowLocalImages, docsHandlers, isDocs, readOnly, toolbarEl]);

    useImperativeHandle(
      ref,
      () => ({
        insertText(text: string) {
          insertAtCursor((editor, index) => {
            editor.insertText(index, text, 'user');
            editor.setSelection(index + text.length, 0);
          });
        },
      }),
      [insertAtCursor],
    );

    const handleChange = useCallback(
      (content: string, _delta: unknown, source: string) => {
        if (source !== 'user') return;
        const next = normalizeQuillHtml(toPersistHtml(content));
        const current = normalizeQuillHtml(value);
        if (next === current) return;
        onChange(next);
      },
      [onChange, value],
    );

    function applyCallout(variant: CalloutVariant): void {
      const editor = quillRef.current?.getEditor();
      if (!editor || readOnly) return;
      const range = editor.getSelection(true);
      const current = range ? editor.getFormat(range) : editor.getFormat();
      if (current.callout === variant) {
        editor.format('callout', false, 'user');
      } else {
        editor.format('callout', variant, 'user');
        const preset = CALLOUT_PRESETS.find((p) => p.variant === variant);
        if (preset && range && range.length === 0) {
          const [line] = editor.getLine(range.index);
          const lineText = line
            ? String((line as { domNode?: HTMLElement }).domNode?.textContent ?? '')
            : '';
          if (!lineText.trim()) {
            editor.insertText(range.index, `${preset.emoji} `, 'user');
          }
        }
      }
      setCalloutOpen(false);
    }

    useEffect(() => {
      if (!calloutOpen) return;

      function onPointer(event: MouseEvent) {
        const target = event.target as Node;
        const menu = rootRef.current?.querySelector('.rte-callout-menu');
        const btn = rootRef.current?.querySelector('button.ql-callout');
        if (menu?.contains(target) || btn?.contains(target)) return;
        setCalloutOpen(false);
      }

      function onKey(event: KeyboardEvent) {
        if (event.key === 'Escape') setCalloutOpen(false);
      }

      document.addEventListener('mousedown', onPointer);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onPointer);
        document.removeEventListener('keydown', onKey);
      };
    }, [calloutOpen]);

    useEffect(() => {
      if (readOnly) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const timer = window.setTimeout(() => {
        editor.focus();
      }, 50);
      return () => window.clearTimeout(timer);
    }, [readOnly, toolbarEl]);

    useEffect(() => {
      if (readOnly) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const quill = editor;

        function syncEmbedEdits(event: Event): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest?.('.ql-collapse, .ql-doc-table-wrap')) return;
        const html = normalizeQuillHtml(toPersistHtml(quill.root.innerHTML));
        onChange(html);
      }

      quill.root.addEventListener('input', syncEmbedEdits);
      quill.root.addEventListener('blur', syncEmbedEdits, true);
      return () => {
        quill.root.removeEventListener('input', syncEmbedEdits);
        quill.root.removeEventListener('blur', syncEmbedEdits, true);
      };
    }, [onChange, readOnly, toolbarEl]);

    useEffect(() => {
      if (readOnly || !pageId) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      let cancelled = false;

      void (async () => {
        const imgs = Array.from(editor.root.querySelectorAll('img'));
        for (const img of imgs) {
          const existingId = img.getAttribute(DOCS_ATTACHMENT_ATTR);
          const fromSrc = parseDocsAttachmentId(img.getAttribute('src'));
          const attachmentId = existingId || fromSrc;
          if (!attachmentId) continue;

          if (!existingId) {
            img.setAttribute(DOCS_ATTACHMENT_ATTR, attachmentId);
          }

          if (!parseDocsAttachmentId(img.getAttribute('src'))) {
            continue;
          }

          try {
            const download = await getDocumentationAttachmentDownloadUrl(pageId, attachmentId);
            if (!cancelled) {
              img.setAttribute('src', download.url);
            }
          } catch {
            // Mantém src original se falhar.
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [pageId, readOnly, toolbarEl, value]);

    useEffect(() => {
      if (readOnly || !allowLocalImages) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      function handlePaste(event: ClipboardEvent): void {
        const clipboard = event.clipboardData;
        if (!clipboard) return;

        const imageFiles: File[] = [];
        for (const item of Array.from(clipboard.items)) {
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) imageFiles.push(file);
          }
        }

        if (imageFiles.length === 0 && clipboard.files?.length) {
          imageFiles.push(...Array.from(clipboard.files).filter(isAcceptedImage));
        }

        if (imageFiles.length === 0) return;

        event.preventDefault();
        event.stopPropagation();
        void uploadOrEmbedImage(imageFiles[0]!);
      }

      editor.root.addEventListener('paste', handlePaste, true);
      return () => {
        editor.root.removeEventListener('paste', handlePaste, true);
      };
    }, [allowLocalImages, readOnly, toolbarEl, uploadOrEmbedImage]);

    function focusEditorFromClick(event: ReactMouseEvent<HTMLDivElement>): void {
      if (readOnly) return;
      const target = event.target as HTMLElement;

      // Portais (modais) borbulham na árvore React; não roubar o foco deles.
      if (rootRef.current && !rootRef.current.contains(target)) {
        return;
      }

      if (
        target.closest('.ql-toolbar') ||
        target.closest('.rte-docs-title') ||
        target.closest('.rte-emoji-picker') ||
        target.closest('.rte-callout-menu') ||
        target.closest('.ql-collapse__body') ||
        target.closest('.ql-collapse__title') ||
        target.closest('.ql-doc-table th, .ql-doc-table td')
      ) {
        return;
      }

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const inEditor = Boolean(target.closest('.ql-editor'));
      if (!inEditor) {
        event.preventDefault();
        editor.focus();
        const length = editor.getLength();
        editor.setSelection(Math.max(length - 1, 0), 0);
      }
    }

    return (
      <div
        ref={rootRef}
        className={`rich-text-editor rounded-lg border border-secondary-300 bg-white${
          isDocs ? ' rich-text-editor--docs' : ''
        }${toolbarPinned ? ' rich-text-editor--toolbar-pinned' : ''}`}
        style={{ ['--rte-min-height' as string]: minHeight }}
        onMouseDown={focusEditorFromClick}
      >
        {isDocs && !readOnly ? (
          <div className="rte-docs-chrome">
            {onTitleChange ? (
              <input
                className="docs-wiki-article__title-input rte-docs-title"
                value={title ?? ''}
                onChange={(e) => onTitleChange(e.target.value)}
                maxLength={300}
                placeholder={titlePlaceholder}
                aria-label="Título da página"
              />
            ) : null}
            <DocumentationEditorToolbar
              ref={setToolbarEl}
              toolbarPinned={toolbarPinned}
              onTogglePin={toggleToolbarPin}
            />
          </div>
        ) : null}

        {(!isDocs || readOnly || toolbarEl) && (
          <ReactQuill
            key={isDocs ? (toolbarEl ? 'docs-ready' : 'docs-wait') : 'default'}
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={handleChange}
            modules={modules}
            formats={isDocs ? DOCUMENTATION_FORMATS : DEFAULT_FORMATS}
            readOnly={readOnly}
            placeholder={placeholder}
          />
        )}

        {isDocs && !readOnly ? (
          <>
            <EmojiPicker
              open={emojiOpen}
              onClose={() => setEmojiOpen(false)}
              anchorRef={rootRef}
              onPick={(emoji) => {
                insertAtCursor((editor, index) => {
                  editor.insertText(index, emoji, 'user');
                  editor.setSelection(index + emoji.length, 0);
                });
              }}
            />

            {calloutOpen ? (
              <div className="rte-callout-menu" role="menu" aria-label="Seção colorida">
                {CALLOUT_PRESETS.map((preset) => (
                  <button
                    key={preset.variant}
                    type="button"
                    role="menuitem"
                    className={`rte-callout-menu__item rte-callout-menu__item--${preset.variant}`}
                    onClick={() => applyCallout(preset.variant)}
                  >
                    <span aria-hidden>{preset.emoji}</span>
                    {preset.label}
                  </button>
                ))}
              </div>
            ) : null}

            <VideoEmbedModal
              open={videoModalOpen}
              onClose={() => setVideoModalOpen(false)}
              onConfirm={(embedUrl) => {
                insertAtCursor((editor, index) => {
                  editor.insertText(index, '\n', 'user');
                  editor.insertEmbed(index + 1, 'video', embedUrl, 'user');
                  editor.insertText(index + 2, '\n', 'user');
                  editor.setSelection(index + 3, 0);
                });
              }}
            />

            <TableInsertModal
              open={tableModalOpen}
              onClose={() => setTableModalOpen(false)}
              onConfirm={(rows, cols) => {
                insertAtCursor((editor, index) => {
                  editor.insertText(index, '\n', 'user');
                  editor.insertEmbed(index + 1, 'doctable', createEmptyTableValue(rows, cols), 'user');
                  editor.insertText(index + 2, '\n', 'user');
                  editor.setSelection(index + 3, 0);
                });
              }}
            />
          </>
        ) : null}
      </div>
    );
  },
);
